/**
 * Resolves insight-banner heading copy from Elite Prime strategy status.
 *
 * Belongs here: status → title / badge mapping for the advice carousel header.
 * Does not belong here: advice-card body stats or allocation-preview chrome.
 */
import {
  ElitePrimeStrategyStatus,
  type ElitePrimeStrategyData
} from '../../../types/elitePrimeSummary.types'
import type { InsightBannerContent } from '../types/plan.types'
import {
  isStrategyFindCopyStatus,
  isStrategyQuestionnaireSubmittedStatus
} from './advice-cards/adviceCardStrategyStatusDisplay'

export type InsightBannerDisplay = {
  title: string
  statusLabel?: string
  statusColor?: string
  /** Ready checkmark — only for PLAN_ROLLED_OUT (and Elite static ready banner). */
  showReadyIcon: boolean
}

/**
 * Maps strategy status to insight-banner heading. When `resolveByStrategy` is
 * false (Elite tab), returns CMS ready copy unchanged.
 */
export function resolveInsightBannerDisplay({
  insightBanner,
  strategyStatus,
  resolveByStrategy
}: {
  insightBanner: InsightBannerContent
  strategyStatus?: ElitePrimeStrategyData['status']
  resolveByStrategy: boolean
}): InsightBannerDisplay {
  if (!resolveByStrategy) {
    return {
      title: insightBanner.title,
      statusLabel: insightBanner.statusLabel,
      statusColor: insightBanner.statusColor,
      showReadyIcon: Boolean(insightBanner.statusLabel)
    }
  }

  const resolvedStatus =
    strategyStatus ?? ElitePrimeStrategyStatus.QUESTIONNAIRE_NOT_INITIATED

  if (isStrategyFindCopyStatus(resolvedStatus)) {
    return {
      title: insightBanner.findStrategy?.title ?? insightBanner.title,
      showReadyIcon: false
    }
  }

  if (isStrategyQuestionnaireSubmittedStatus(resolvedStatus)) {
    return {
      title: insightBanner.submitted?.title ?? insightBanner.title,
      statusLabel: insightBanner.submitted?.statusLabel,
      statusColor: insightBanner.statusColor,
      showReadyIcon: false
    }
  }

  // PLAN_ROLLED_OUT
  return {
    title: insightBanner.title,
    statusLabel: insightBanner.statusLabel,
    statusColor: insightBanner.statusColor,
    showReadyIcon: Boolean(insightBanner.statusLabel)
  }
}
